import {
  AVAILABILITY_SLOTS,
  experienceRank,
  experienceTier,
  isExperienced,
  MIN_GAP_MINUTES,
  MIN_SHIFT_MINUTES,
  type AvailabilitySlot,
  type AvailabilityValue,
  type ExperienceTier,
  type PostType,
} from './constants';
import type { WindowLike } from './conflicts';
import type { ShiftPlanning } from './schemas/planning';
import { availabilityCuts, handoverCuts, splitRange } from './split';
import { gapBetween, MINUTES_PER_DAY, rangesOverlap, type MinuteRange } from './time';

/**
 * Gap autofill (SPEC §4.3). This is the shift-minimisation personnel task
 * scheduling problem — fixed-time tasks, workers limited by availability and
 * capacity — which is NP-hard, so this stays a heuristic and every result is a
 * proposal the coordinator accepts or rejects. Greedy construction is known to
 * land close to optimal on instances this size.
 *
 * The scarce resource is a volunteer who is free only in one part of the day.
 * If a morning-only person is not used in the morning they are not used at
 * all, while someone free all day can be moved. So scarcity drives both halves
 * of the greedy choice:
 *
 *  1. **Which piece next** — the one with the fewest people who could take it.
 *     Filling the tightest slot first stops an early, easy assignment from
 *     consuming the only person who could have covered a hard one later.
 *  2. **Who takes it** — the most restricted candidate; then, among equals,
 *     whoever is not yet briefed, because adding a shift to someone who has
 *     already had their briefing costs them a re-confirmation and adding one
 *     to a volunteer nobody has written to yet costs nothing; then whoever it
 *     fills up most neatly, so a day is spent rather than scattered.
 *
 * On an empty day nobody is briefed yet, so the middle term is inert and the
 * pass fills people up. Topping up a roster that is already out with people is
 * where it bites, and there it steers work toward whoever is cheapest to ask.
 *
 * That construction runs three times, under a rising ceiling and a widening
 * pool:
 *
 *  - **Round 1** stops at `softDailyMin`, the day a volunteer is meant to get.
 *    As the people it has used reach that, they stop being candidates, so the
 *    pass is forced outward into the pool — which is what gets work to
 *    everyone who signed up rather than a long day to a few.
 *  - **Round 2a** takes only what round 1 could not place, raises the ceiling
 *    to `maxDailyMin`, and offers it to the volunteers who are here for one
 *    day only. A long day costs them nothing they would otherwise give: a
 *    volunteer coming back tomorrow and on Sunday spends the same hours across
 *    three days, so their Saturday is worth more kept near the recommendation.
 *  - **Round 2b** takes what even that left and opens the ceiling to everyone.
 *    So a multi-day volunteer does get a long day when the alternative is an
 *    empty post — the one-day preference never costs coverage.
 *
 * Which round a piece lands in is the whole mechanism for that preference:
 * `rank` is untouched by it, so scarcity keeps comparing candidates normally
 * inside each round. It shapes who is in the pool, never how the pool is
 * ordered — the same boundary the urgent-post rule below observes, and for the
 * same reason.
 *
 * The earlier rounds never report why they dropped a piece: it would read "at
 * capacity" for work a later round then places. Only the last round's counts
 * are published. Setting the two ceilings equal collapses all of this back to
 * a single pass that fills each person up before reaching for the next.
 *
 * Then a repair pass tries to place what both rounds missed by moving one
 * assignment out of the way.
 *
 * A post the coordinator marked **nujen** is staffed from the experienced part
 * of the pool first: `zelo izkušen(a)`, then `izkušen(a)`, then an unassessed
 * volunteer, then `neizkušen(a)`. That is a preference and it lives entirely in
 * `rank` — it changes who takes a piece, never which piece is taken next.
 *
 * That boundary is the whole safety property. Which piece goes next is decided
 * by how few people could take it, and that count is what stops an easy
 * assignment from consuming the only person who could have covered a hard one.
 * Urgency has to stay out of it: weighting that count — counting only an urgent
 * piece's experienced candidates, say — measures two pieces on different scales
 * and costs coverage, on roughly one instance in a hundred, an unstaffed
 * *urgent* post on a third of those. Ranking carries the preference on its own
 * at some 97 % of the same effect and none of that cost.
 * `test/autofill-differential.test.ts` holds the line.
 *
 * Inside a piece the term sits above the scarcity measure, because scarcity is
 * measured in minutes and so rarely ties that anything below it would never
 * decide anything, and below the availability verdict, because what a volunteer
 * told us about their own day is not ours to trade for a better assessment.
 * Scarcity still chooses *within* a tier: an urgent piece gets the most
 * restricted expert, not whichever one sorts first.
 *
 * Ordinary posts are untouched, nothing is reserved for an urgent piece that
 * has not come up yet, and no urgent piece is left empty for want of an expert.
 * When the preference does come up empty the proposal says so, so the choice is
 * one the coordinator makes rather than one the pass hides.
 *
 * Lives in `shared` because both the board (which previews the plan) and the
 * API (which validates and applies it) have to agree on what was proposed.
 *
 * Two gates decide who may be proposed at all:
 *  - **Eligibility** is absolute. Someone who turned down a shift on the day
 *    being filled, who did not tell us they are free for every signup slot the
 *    piece touches, or who would pass the daily cap, is never proposed.
 *  - **Scheduling rules** split in two. An overlap is absolute: one person
 *    cannot stand at two posts at once. The 1 h turnaround is a judgement call
 *    — it blocks a proposal while any clean candidate remains, and only when
 *    the piece would otherwise go empty does a candidate get proposed with a
 *    warning. SPEC's "surface, never hard-block" applies to that one.
 */

export interface AutofillVolunteer {
  id: string;
  name: string;
  /** AVAILABILITY_SLOTS id → value; a missing slot means the person never answered. */
  availability: Record<string, AvailabilityValue>;
  /**
   * They turned down a shift on the day being filled — one they hold, or one
   * freed since (SPEC §1.1F). A refusal on another day says nothing about this
   * one, so it is asked per day rather than read off the person's status.
   */
  declined: boolean;
  /**
   * The coordinators' assessment, verbatim from the signup sheet's "ocenjena
   * izkušenost" column — `''` when nobody assessed them. Only urgent posts
   * read it; `experienceTier` is what interprets it.
   */
  experience: string;
  /**
   * Whether they said they can help on more than one event day ("lahko pomaga
   * večkrat"), `null` when they left it blank. Read only when the ceiling
   * rises past `softDailyMin` — see `isSingleDay`.
   */
  canMultiDay: boolean | null;
  /** Minutes already assigned to this person on the day being filled. */
  dayMinutes: number;
  /**
   * Their briefing has gone out for the schedule they currently hold. Adding a
   * shift bumps their version, which retires that briefing and puts them back
   * to `čaka` — so touching them costs a re-confirmation and touching an
   * un-briefed volunteer costs nothing.
   */
  briefed: boolean;
}

export interface AutofillShift extends MinuteRange {
  id: string;
  postId: string;
  volunteerId: string | null;
  state: string;
}

export interface AutofillPost {
  id: string;
  code: string;
  type: PostType;
  urgent: boolean;
  /** Uncovered stretches of this post's windows, from `computeGaps`. */
  gaps: WindowLike[];
  /** The day's shifts at this post — their edges are preferred cut points. */
  shifts: MinuteRange[];
  /**
   * Unassigned slots the coordinator already cut at this post. They read as
   * uncovered window, so autofill staffs the row that is already there
   * instead of laying a second shift on top of it.
   */
  openShifts: { id: string; fromMin: number; toMin: number }[];
}

export interface AutofillInput {
  /** Availability day key of the day being filled — `d0` | `d1` | `d2`. */
  dayKey: string;
  planning: ShiftPlanning;
  posts: AutofillPost[];
  volunteers: AutofillVolunteer[];
  /** Every non-cancelled shift on this day, across all posts. */
  shifts: AutofillShift[];
}

/** One shift-sized piece of an uncovered window — what actually gets assigned. */
export interface AutofillGap extends MinuteRange {
  postId: string;
  postCode: string;
  urgent: boolean;
  /** An existing open shift to staff, or null when a new one has to be created. */
  targetShiftId: string | null;
}

/** Something about the assignment the coordinator should weigh before taking it. */
export type AutofillWarning =
  /** The 1 h turnaround, bent because nobody clean was free. */
  | { kind: 'short-gap'; min: number }
  /** An urgent post that no experienced volunteer was left to take. */
  | { kind: 'inexperienced'; tier: ExperienceTier };

export interface AutofillProposal {
  gap: AutofillGap;
  volunteerId: string;
  volunteerName: string;
  availability: AvailabilityVerdict;
  warnings: AutofillWarning[];
}

/** Why a piece got no proposal, with the pool counted by cause. */
export interface AutofillUnfilled extends AutofillGap {
  reason: 'no-pool' | 'not-available' | 'declined' | 'at-capacity' | 'busy';
  counts: { available: number; notAvailable: number; declined: number; atCapacity: number };
}

/** What the plan asks of each person it uses — the coordinator's sanity check. */
export interface AutofillUtilisation {
  volunteerId: string;
  volunteerName: string;
  /** Minutes on the day once the plan is accepted, existing shifts included. */
  plannedMin: number;
  /** Minutes the person told us they are free. */
  availableMin: number;
  proposedCount: number;
  /** Accepting the plan retires this person's briefing and asks them again. */
  needsReconfirm: boolean;
  /** Their day runs past `softDailyMin` — round 2 had nobody else to ask. */
  overSoft: boolean;
}

export interface AutofillPlan {
  proposals: AutofillProposal[];
  unfilled: AutofillUnfilled[];
  utilisation: AutofillUtilisation[];
}

/**
 * How a volunteer's signup answers cover a time range:
 *  - `yes` — free for every slot the range touches
 *  - `partial` — free, but marked "delno" ("not the whole slot") somewhere
 *  - `no-data` — the sheet never asked about this time (Friday 8h–12h,
 *    Sunday after 16h); no signal either way
 *  - `unknown` — the sheet asked and the person left it blank or "neznano"
 *  - `no` — the person marked at least one touched slot "ni na voljo"
 */
export type AvailabilityVerdict = 'yes' | 'partial' | 'no-data' | 'unknown' | 'no';

export function availabilityFor(
  availability: Record<string, AvailabilityValue>,
  dayKey: string,
  range: MinuteRange,
): AvailabilityVerdict {
  const touched = AVAILABILITY_SLOTS.filter((s) => s.dayKey === dayKey && rangesOverlap(range, s));
  if (touched.length === 0) return 'no-data';

  let verdict: AvailabilityVerdict = 'yes';
  for (const slot of touched) {
    const answer = availability[slot.id];
    if (answer === 'ni-na-voljo') return 'no';
    if (answer === undefined || answer === 'neznano') verdict = 'unknown';
    else if (answer === 'delno' && verdict === 'yes') verdict = 'partial';
  }
  return verdict;
}

/**
 * Eligible to be proposed at all. The strict reading: only an explicit "I am
 * free" counts, so a blank answer excludes just as "ni na voljo" does. Times
 * the sheet never asked about cannot exclude anyone.
 */
export function isEligible(verdict: AvailabilityVerdict): boolean {
  return verdict === 'yes' || verdict === 'partial' || verdict === 'no-data';
}

/**
 * The slots `range` runs into that this person marked "ni na voljo".
 *
 * Deliberately *not* {@link availabilityFor}. That one decides eligibility and
 * is absolute on purpose (SPEC §3): a single touched minute of a closed slot
 * excludes a candidate, which is safe because excluding one costs nothing. This
 * answers a different question — is a coordinator about to do something they
 * would want to know about — and there the cost is inverted: a warning that
 * fires on a technicality stops being read. So a touch shorter than the
 * shortest izmena the board will draw does not count.
 *
 * On the 2026 posts that threshold is the whole difference between signal and
 * noise: ten Saturday shifts run 07:50–08:30 and clip "zjutraj (5h–8h)" by ten
 * minutes, while every other touch in the schedule is half an hour or more.
 * A shift shorter than the threshold is measured against its own length
 * instead, so a 30-minute shift lying wholly inside a closed slot still warns.
 *
 * A blank or "neznano" answer never warns: 28 of the 98 volunteers in the 2026
 * sheet left it entirely empty, and nagging about a third of the roster on
 * every assignment teaches coordinators to click past the warning.
 *
 * Returned in the order they occur, so the caller can name them left to right.
 */
export function unavailableSlots(
  availability: Record<string, AvailabilityValue>,
  dayKey: string,
  range: MinuteRange,
): AvailabilitySlot[] {
  const floor = Math.min(MIN_SHIFT_MINUTES, range.toMin - range.fromMin);
  if (floor <= 0) return [];
  return AVAILABILITY_SLOTS.filter(
    (s) =>
      s.dayKey === dayKey &&
      availability[s.id] === 'ni-na-voljo' &&
      Math.min(range.toMin, s.toMin) - Math.max(range.fromMin, s.fromMin) >= floor,
  ).sort((a, b) => a.fromMin - b.fromMin);
}

/** What is left of `range` once every slot the person closed is cut out of it. */
export function availableWithin(
  availability: Record<string, AvailabilityValue>,
  dayKey: string,
  range: MinuteRange,
): MinuteRange[] {
  return subtractRanges(range, unavailableSlots(availability, dayKey, range));
}

const VERDICT_RANK: Record<AvailabilityVerdict, number> = {
  yes: 0,
  partial: 1,
  'no-data': 2,
  unknown: 3,
  no: 4,
};

/**
 * How much of the day a person offered — the scarcity measure. Someone who
 * answered nothing is not known to be constrained, so they count as wide open
 * and never outrank a genuinely restricted volunteer.
 */
export function availableMinutes(
  availability: Record<string, AvailabilityValue>,
  dayKey: string,
): number {
  const slots = AVAILABILITY_SLOTS.filter((s) => s.dayKey === dayKey);
  if (slots.every((s) => availability[s.id] === undefined)) return MINUTES_PER_DAY;
  return slots
    .filter((s) => {
      const a = availability[s.id];
      return a === 'na-voljo' || a === 'delno';
    })
    .reduce((total, s) => total + (s.toMin - s.fromMin), 0);
}

/** Shifts that occupy a person's time; cancelled ones are filtered out upstream. */
function occupies(s: AutofillShift): boolean {
  return s.volunteerId !== null && s.state !== 'cancelled';
}

function heldBy(volunteerId: string, shifts: AutofillShift[]): AutofillShift[] {
  return shifts.filter((s) => occupies(s) && s.volunteerId === volunteerId);
}

/** Does [fromMin,toMin] clash with something this person already holds? */
export function wouldOverlap(
  volunteerId: string,
  range: MinuteRange,
  shifts: AutofillShift[],
): boolean {
  return heldBy(volunteerId, shifts).some((s) => rangesOverlap(range, s));
}

/** The tightest turnaround this assignment would create, if it is under 1 h. */
export function assignmentWarnings(
  volunteerId: string,
  range: MinuteRange,
  shifts: AutofillShift[],
): AutofillWarning[] {
  const tightest = heldBy(volunteerId, shifts)
    .filter((s) => !rangesOverlap(range, s))
    .map((s) => gapBetween(range, s))
    .filter((gap) => gap > 0 && gap < MIN_GAP_MINUTES)
    .sort((a, b) => a - b)
    .at(0);
  return tightest === undefined ? [] : [{ kind: 'short-gap', min: tightest }];
}

/** What is left of `base` once every range in `cuts` is removed from it. */
export function subtractRanges(base: MinuteRange, cuts: MinuteRange[]): MinuteRange[] {
  let segments: MinuteRange[] = [{ ...base }];
  for (const cut of cuts) {
    const next: MinuteRange[] = [];
    for (const seg of segments) {
      if (!rangesOverlap(seg, cut)) {
        next.push(seg);
        continue;
      }
      if (cut.fromMin > seg.fromMin) next.push({ fromMin: seg.fromMin, toMin: cut.fromMin });
      if (cut.toMin < seg.toMin) next.push({ fromMin: cut.toMin, toMin: seg.toMin });
    }
    segments = next;
  }
  // Slivers left either side of an open shift are noise, not work.
  return segments.filter((s) => s.toMin - s.fromMin >= 5);
}

/**
 * The day's uncovered windows, cut into shift-sized pieces. An open slot the
 * coordinator already made is one piece exactly as they drew it; the window
 * around it is split normally. Urgent posts lead so ties resolve toward them.
 */
export function gapQueue(input: AutofillInput): AutofillGap[] {
  const dayCuts = availabilityCuts(input.dayKey);
  return input.posts
    .flatMap((p) => {
      const preferred = [...handoverCuts(p.shifts), ...dayCuts];
      const fresh = p.gaps.flatMap((g) =>
        subtractRanges(g, p.openShifts).flatMap((remainder) =>
          splitRange(remainder, input.planning, p.type, preferred).map((piece) => ({
            fromMin: piece.fromMin,
            toMin: piece.toMin,
            targetShiftId: null as string | null,
          })),
        ),
      );
      const existing = p.openShifts.map((o) => ({
        fromMin: o.fromMin,
        toMin: o.toMin,
        targetShiftId: o.id,
      }));
      return [...fresh, ...existing].map((piece) => ({
        postId: p.id,
        postCode: p.code,
        urgent: p.urgent,
        ...piece,
      }));
    })
    .sort(byPriority);
}

/** Queue order: urgent posts lead, then earliest, then by post code. */
function byPriority(a: AutofillGap, b: AutofillGap): number {
  return (
    Number(b.urgent) - Number(a.urgent) ||
    a.fromMin - b.fromMin ||
    a.postCode.localeCompare(b.postCode, 'sl')
  );
}

interface Candidate {
  volunteer: AutofillVolunteer;
  verdict: AvailabilityVerdict;
  tier: ExperienceTier;
  /**
   * Turnaround warnings only. Inexperience does not belong here: bending the
   * 1 h rest costs the volunteer something and so gates them out of `clean`,
   * while being a novice only loses a preference. `proposalWarnings` adds that
   * one once a candidate has actually been picked.
   */
  warnings: AutofillWarning[];
}

interface Assessment {
  clean: Candidate[];
  warned: Candidate[];
  counts: AutofillUnfilled['counts'];
}

/**
 * Everyone who could take this piece under `cap`, and why the rest could not.
 * The ceiling is a parameter because the two construction rounds differ in
 * nothing else.
 */
function assess(
  gap: AutofillGap,
  input: AutofillInput,
  shifts: AutofillShift[],
  load: Map<string, number>,
  tiers: Map<string, ExperienceTier>,
  cap: number,
  skip?: string,
): Assessment {
  const counts = { available: 0, notAvailable: 0, declined: 0, atCapacity: 0 };
  const clean: Candidate[] = [];
  const warned: Candidate[] = [];
  const duration = gap.toMin - gap.fromMin;

  for (const volunteer of input.volunteers) {
    if (volunteer.id === skip) continue;
    if (volunteer.declined) {
      counts.declined += 1;
      continue;
    }
    const verdict = availabilityFor(volunteer.availability, input.dayKey, gap);
    if (!isEligible(verdict)) {
      counts.notAvailable += 1;
      continue;
    }
    if ((load.get(volunteer.id) ?? 0) + duration > cap) {
      counts.atCapacity += 1;
      continue;
    }
    counts.available += 1;
    // A clash disqualifies rather than warns, but the person still counts as
    // available so the piece reads "busy" rather than "nobody free".
    if (wouldOverlap(volunteer.id, gap, shifts)) continue;
    const warnings = assignmentWarnings(volunteer.id, gap, shifts);
    const tier = tiers.get(volunteer.id) ?? 'neznano';
    (warnings.length === 0 ? clean : warned).push({ volunteer, verdict, tier, warnings });
  }
  return { clean, warned, counts };
}

/**
 * Who the piece is served from: the candidates that bend no rule, or — only
 * when there are none — the ones that bend the turnaround. A copy, because
 * callers rank it and `Array.sort` is in place; the assessment it came from
 * still has to read as it was assessed.
 */
function poolOf(a: Assessment): Candidate[] {
  return [...(a.clean.length > 0 ? a.clean : a.warned)];
}

/**
 * How many that is. Its own function because the tightest-piece scan asks it
 * for every pending piece on every iteration, and copying a pool to measure it
 * would be the most expensive thing the planner does.
 */
function poolSize(a: Assessment): number {
  return a.clean.length > 0 ? a.clean.length : a.warned.length;
}

/**
 * Rank candidates for one piece: honour what people told us first, prefer an
 * experienced volunteer where the post is urgent, spend the most restricted
 * person's time next, then reach for whoever costs no re-confirmation, and
 * only then fill somebody up.
 *
 * The experience term sits above scarcity because scarcity is measured in
 * minutes and so almost never ties — anything below it would never decide
 * anything. It sits below the availability verdict because what a volunteer
 * told us about their own day is not ours to trade away for a better CV.
 */
function rank(load: Map<string, number>, breadth: Map<string, number>, gap: AutofillGap, cap: number) {
  const duration = gap.toMin - gap.fromMin;
  const leftover = (c: Candidate) => cap - (load.get(c.volunteer.id) ?? 0) - duration;
  const experience = (c: Candidate) => (gap.urgent ? experienceRank(c.tier) : 0);
  return (a: Candidate, b: Candidate) =>
    VERDICT_RANK[a.verdict] - VERDICT_RANK[b.verdict] ||
    experience(a) - experience(b) ||
    (breadth.get(a.volunteer.id) ?? 0) - (breadth.get(b.volunteer.id) ?? 0) ||
    Number(a.volunteer.briefed) - Number(b.volunteer.briefed) ||
    leftover(a) - leftover(b) ||
    a.volunteer.name.localeCompare(b.volunteer.name, 'sl');
}

/**
 * What the coordinator is asked to weigh about this assignment: the turnaround
 * it bends, and — on an urgent post only — that the pass could not reach an
 * experienced volunteer. The second is not a rule being broken but the
 * preference coming up empty, which is exactly the call a coordinator wants in
 * front of them on the Predlog tab rather than buried in an accepted batch.
 */
function proposalWarnings(gap: AutofillGap, pick: Candidate): AutofillWarning[] {
  if (!gap.urgent || isExperienced(pick.tier)) return pick.warnings;
  return [...pick.warnings, { kind: 'inexperienced', tier: pick.tier }];
}

/**
 * How one person stands against one piece, for a coordinator choosing by hand
 * rather than for the planner choosing for them. `rankPiece` is what the board's
 * candidate list and `Oseba` picker read.
 *
 * `group` is the heaviest thing true of them:
 *  - `busy` — they already stand somewhere in that time; not assignable.
 *  - `flag` — overrules something: a shift they turned down that day, a slot
 *    they marked "ni na voljo", or a day past `maxDailyMin`.
 *  - `warn` — the planner would not take them cleanly: a blank answer, a
 *    closed slot the piece only clips, a turnaround under 1 h, a day past the
 *    recommended one, or a post marked nujen they are not assessed for.
 *  - `rec` — who the planner itself could pick without a word.
 */
export type PieceGroup = 'rec' | 'warn' | 'flag' | 'busy';

export type PieceReason =
  | { kind: 'available' }
  | { kind: 'partial' }
  | { kind: 'no-data' }
  | { kind: 'unknown' }
  /** The piece touches a closed slot for less than the shortest shift. */
  | { kind: 'clips-closed' }
  | { kind: 'closed'; slots: AvailabilitySlot[] }
  | { kind: 'declined' }
  | { kind: 'over-max'; afterMin: number }
  | { kind: 'over-soft'; afterMin: number }
  | { kind: 'short-gap'; min: number }
  | { kind: 'experienced-urgent' }
  | { kind: 'inexperienced-urgent'; tier: ExperienceTier };

export interface PieceCandidate {
  volunteer: AutofillVolunteer;
  group: PieceGroup;
  verdict: AvailabilityVerdict;
  tier: ExperienceTier;
  reasons: PieceReason[];
  /** Minutes they hold on the day now, and with this piece. */
  beforeMin: number;
  afterMin: number;
}

const GROUP_ORDER: Record<PieceGroup, number> = { rec: 0, warn: 1, flag: 2, busy: 3 };

/**
 * Everyone in `input.volunteers` against one piece, grouped and ordered the way
 * the planner orders a pool: within a group by `rank`, so the first `rec` is
 * the person autofill would propose for this piece alone.
 *
 * `skipVolunteerId` leaves out the person the piece belongs to now — a shift's
 * holder is not a candidate for their own shift.
 */
export function rankPiece(
  input: AutofillInput,
  piece: MinuteRange & { postId: string; urgent: boolean },
  opts: { skipVolunteerId?: string } = {},
): PieceCandidate[] {
  const { maxDailyMin } = input.planning;
  const softDailyMin = Math.min(input.planning.softDailyMin, maxDailyMin);
  const duration = piece.toMin - piece.fromMin;
  const load = new Map(input.volunteers.map((v) => [v.id, v.dayMinutes]));
  const breadth = new Map(
    input.volunteers.map((v) => [v.id, availableMinutes(v.availability, input.dayKey)]),
  );
  const out: PieceCandidate[] = [];

  for (const volunteer of input.volunteers) {
    if (volunteer.id === opts.skipVolunteerId) continue;
    const verdict = availabilityFor(volunteer.availability, input.dayKey, piece);
    const tier = experienceTier(volunteer.experience);
    const closed = unavailableSlots(volunteer.availability, input.dayKey, piece);
    const beforeMin = volunteer.dayMinutes;
    const afterMin = beforeMin + duration;
    const flags: PieceReason[] = [];
    const warns: PieceReason[] = [];
    const good: PieceReason[] = [];

    if (volunteer.declined) flags.push({ kind: 'declined' });
    if (closed.length > 0) flags.push({ kind: 'closed', slots: closed });
    else if (verdict === 'no') warns.push({ kind: 'clips-closed' });
    if (afterMin > maxDailyMin) flags.push({ kind: 'over-max', afterMin });
    else if (afterMin > softDailyMin) warns.push({ kind: 'over-soft', afterMin });
    if (verdict === 'unknown') warns.push({ kind: 'unknown' });
    for (const w of assignmentWarnings(volunteer.id, piece, input.shifts)) {
      if (w.kind === 'short-gap') warns.push(w);
    }
    if (piece.urgent) {
      if (isExperienced(tier)) good.push({ kind: 'experienced-urgent' });
      else warns.push({ kind: 'inexperienced-urgent', tier });
    }
    if (verdict === 'yes') good.unshift({ kind: 'available' });
    else if (verdict === 'partial') good.unshift({ kind: 'partial' });
    else if (verdict === 'no-data') good.unshift({ kind: 'no-data' });

    const group: PieceGroup = wouldOverlap(volunteer.id, piece, input.shifts)
      ? 'busy'
      : flags.length > 0
        ? 'flag'
        : warns.length > 0
          ? 'warn'
          : 'rec';
    out.push({
      volunteer,
      group,
      verdict,
      tier,
      reasons: [...flags, ...warns, ...good],
      beforeMin,
      afterMin,
    });
  }

  const gap: AutofillGap = { ...piece, postCode: '', targetShiftId: null };
  const byRank = rank(load, breadth, gap, softDailyMin);
  return out.sort(
    (a, b) =>
      GROUP_ORDER[a.group] - GROUP_ORDER[b.group] ||
      byRank(
        { volunteer: a.volunteer, verdict: a.verdict, tier: a.tier, warnings: [] },
        { volunteer: b.volunteer, verdict: b.verdict, tier: b.tier, warnings: [] },
      ),
  );
}

/**
 * The piece autofill would cut first from an uncovered `target` at a post: the
 * same split, pulled onto the same handovers and signup-slot edges. A target no
 * longer than the post type's longest shift is returned whole.
 */
export function suggestPiece(
  target: MinuteRange,
  planning: ShiftPlanning,
  type: PostType,
  dayKey: string,
  postShifts: MinuteRange[],
): MinuteRange {
  const preferred = [...handoverCuts(postShifts), ...availabilityCuts(dayKey)];
  return splitRange(target, planning, type, preferred)[0] ?? { ...target };
}

/** One place a person could stand, as the lens on the board draws it. */
export interface PersonFit<T> {
  target: T;
  /** What they would be given: an open row whole, or a piece of a gap. */
  piece: MinuteRange;
  candidate: PieceCandidate;
  /** How many the planner could pick cleanly for that piece, them included. */
  recCount: number;
  /** They are the only one. */
  only: boolean;
}

/**
 * Where one person could go on the day: every uncovered `target` they can take
 * without overruling anything, with the piece they would get. The reverse of
 * `rankPiece`, which asks who could take one piece.
 *
 * An open row (`shiftId`) the coordinator drew is offered whole or not at all,
 * as autofill and the gap list fill it. A gap is cut to the hours they did not
 * close and that keep an hour clear of what they already hold, and the longest
 * such stretch is split the way the gap list suggests, then shortened to what
 * their daily maximum leaves. A piece that would turn a sheet or a refusal
 * down, overrun the maximum or clash (`flag`, `busy`) is not a fit; a warning
 * is, and says so.
 *
 * Ordered by how much it matters that it is them: the only candidate first,
 * then urgent posts, then pieces fewer people could take cleanly, then time.
 */
export function personFits<T extends MinuteRange & { postId: string; shiftId?: string }>(
  input: AutofillInput,
  volunteerId: string,
  targets: T[],
): PersonFit<T>[] {
  const volunteer = input.volunteers.find((v) => v.id === volunteerId);
  if (!volunteer || volunteer.declined) return [];
  const { planning, dayKey } = input;
  const left = planning.maxDailyMin - volunteer.dayMinutes;
  const around = heldBy(volunteerId, input.shifts).map((s) => ({
    fromMin: s.fromMin - MIN_GAP_MINUTES,
    toMin: s.toMin + MIN_GAP_MINUTES,
  }));
  const out: (PersonFit<T> & { urgent: boolean; code: string })[] = [];

  for (const target of targets) {
    const post = input.posts.find((p) => p.id === target.postId);
    if (!post) continue;
    let piece: MinuteRange;
    if (target.shiftId) {
      piece = { fromMin: target.fromMin, toMin: target.toMin };
    } else {
      const least = Math.min(planning.minPieceMin, target.toMin - target.fromMin);
      const stretch = availableWithin(volunteer.availability, dayKey, target)
        .flatMap((r) => subtractRanges(r, around))
        .filter((r) => r.toMin - r.fromMin >= least)
        .sort((a, b) => b.toMin - b.fromMin - (a.toMin - a.fromMin) || a.fromMin - b.fromMin)
        .at(0);
      if (!stretch) continue;
      const postShifts = input.shifts.filter((s) => s.postId === post.id && occupies(s));
      const cut = suggestPiece(stretch, planning, post.type, dayKey, postShifts);
      piece = { fromMin: cut.fromMin, toMin: cut.toMin };
      if (piece.toMin - piece.fromMin > left) {
        piece = { fromMin: piece.fromMin, toMin: piece.fromMin + Math.floor(left / 15) * 15 };
      }
      if (piece.toMin - piece.fromMin < least) continue;
    }
    if (piece.toMin - piece.fromMin > left) continue;

    const ranked = rankPiece(input, { ...piece, postId: post.id, urgent: post.urgent });
    const candidate = ranked.find((c) => c.volunteer.id === volunteerId);
    if (!candidate || (candidate.group !== 'rec' && candidate.group !== 'warn')) continue;
    const rec = ranked.filter((c) => c.group === 'rec');
    out.push({
      target,
      piece,
      candidate,
      recCount: rec.length,
      only: rec.length === 1 && rec[0]!.volunteer.id === volunteerId,
      urgent: post.urgent,
      code: post.code,
    });
  }

  return out
    .sort(
      (a, b) =>
        Number(b.only) - Number(a.only) ||
        Number(b.urgent) - Number(a.urgent) ||
        a.recCount - b.recCount ||
        a.piece.fromMin - b.piece.fromMin ||
        a.code.localeCompare(b.code),
    )
    .map(({ urgent: _urgent, code: _code, ...fit }) => fit);
}

/**
 * Here for one day only, as the volunteer said on the signup sheet. A blank
 * answer is not a "no": stretching somebody's day past the recommended one is
 * not something to do on an assumption, so unstated stands with the multi-day
 * people and is reached in the last round like them.
 */
export function isSingleDay(v: AutofillVolunteer): boolean {
  return v.canMultiDay === false;
}

/**
 * Does this proposal bend one of the scheduling rules? Only the turnaround
 * does. An urgent post that got no experienced volunteer is worth telling the
 * coordinator about, but nothing about it is irregular — surfaces that mark a
 * broken rule (the board's warn state) must ask this rather than count
 * warnings, or a lawful assignment gets painted as a violation.
 */
export function bendsARule(p: AutofillProposal): boolean {
  return p.warnings.some((w) => w.kind === 'short-gap');
}

function proposedShift(gap: AutofillGap, volunteerId: string): AutofillShift {
  return {
    id: `proposal:${gap.postId}:${gap.fromMin}`,
    postId: gap.postId,
    fromMin: gap.fromMin,
    toMin: gap.toMin,
    volunteerId,
    state: 'proposed',
  };
}

/** What one construction round could not place, with the pool counted by cause. */
interface Leftover {
  gap: AutofillGap;
  counts: AutofillUnfilled['counts'];
}

/**
 * One greedy round under a fixed daily ceiling: repeatedly take the piece with
 * the fewest candidates and give it to the best-ranked of them. Returns what
 * it could not place, in queue order so a following round is deterministic.
 */
function construct(
  queue: AutofillGap[],
  input: AutofillInput,
  shifts: AutofillShift[],
  load: Map<string, number>,
  breadth: Map<string, number>,
  tiers: Map<string, ExperienceTier>,
  cap: number,
  take: (gap: AutofillGap, pick: Candidate) => void,
): Leftover[] {
  const pending = [...queue];
  const leftovers: Leftover[] = [];

  while (pending.length > 0) {
    let chosen = -1;
    let chosenAssessment: Assessment | null = null;
    let fewest = Infinity;

    for (let i = 0; i < pending.length; i += 1) {
      const a = assess(pending[i]!, input, shifts, load, tiers, cap);
      const options = poolSize(a);
      if (options < fewest) {
        fewest = options;
        chosen = i;
        chosenAssessment = a;
        if (fewest === 0) break; // nothing is tighter than impossible
      }
    }

    const gap = pending.splice(chosen, 1)[0]!;
    const { counts } = chosenAssessment!;
    const pool = poolOf(chosenAssessment!);
    if (pool.length === 0) {
      leftovers.push({ gap, counts });
      continue;
    }
    take(gap, pool.sort(rank(load, breadth, gap, cap))[0]!);
  }

  return leftovers.sort((a, b) => byPriority(a.gap, b.gap));
}

/** The mutable half of a plan under construction. */
interface Building {
  shifts: AutofillShift[];
  load: Map<string, number>;
  proposals: Map<AutofillGap, AutofillProposal>;
}

const branchOf = (b: Building): Building => ({
  shifts: [...b.shifts],
  load: new Map(b.load),
  proposals: new Map(b.proposals),
});

const coveredMin = (b: Building) =>
  [...b.proposals.keys()].reduce((a, g) => a + (g.toMin - g.fromMin), 0);

const urgentUnfilledMin = (unfilled: AutofillUnfilled[]) =>
  unfilled.filter((g) => g.urgent).reduce((a, g) => a + (g.toMin - g.fromMin), 0);

export function planAutofill(input: AutofillInput): AutofillPlan {
  const breadth = new Map(
    input.volunteers.map((v) => [v.id, availableMinutes(v.availability, input.dayKey)]),
  );
  // A per-person constant, like `breadth`. `assess` reads it once per candidate
  // per scanned piece — tens of thousands of times on a real day — so the free
  // text is normalised here, once.
  const tiers = new Map(input.volunteers.map((v) => [v.id, experienceTier(v.experience)]));
  const { maxDailyMin } = input.planning;
  // The hard cap always wins: a stored setting can be out of order (a row
  // edited by hand, or one written before the recommended day existed) and
  // round 1 must never hand out a longer day than round 2 is allowed to.
  const softDailyMin = Math.min(input.planning.softDailyMin, maxDailyMin);

  const take = (b: Building) => (gap: AutofillGap, pick: Candidate) => {
    b.proposals.set(gap, {
      gap,
      volunteerId: pick.volunteer.id,
      volunteerName: pick.volunteer.name,
      availability: pick.verdict,
      warnings: proposalWarnings(gap, pick),
    });
    b.load.set(pick.volunteer.id, (b.load.get(pick.volunteer.id) ?? 0) + (gap.toMin - gap.fromMin));
    b.shifts.push(proposedShift(gap, pick.volunteer.id));
  };

  const round = (b: Building, queue: AutofillGap[], cap: number, pool = input.volunteers) =>
    construct(queue, { ...input, volunteers: pool }, b.shifts, b.load, breadth, tiers, cap, take(b));

  // Round 1 gives everyone the recommended day before anyone gets a long one.
  const base: Building = {
    shifts: [...input.shifts],
    load: new Map(input.volunteers.map((v) => [v.id, v.dayMinutes])),
    proposals: new Map(),
  };
  const afterFirst = round(base, gapQueue(input), softDailyMin);

  /**
   * Everything past round 1: raise the ceiling for what is left, then try to
   * place the remainder by moving one assignment aside. `oneDayFirst` offers
   * the raised ceiling to the volunteers who are here for a single day before
   * anyone else sees it.
   */
  const finish = (oneDayFirst: boolean) => {
    const b = branchOf(base);
    let leftovers = afterFirst;
    if (maxDailyMin > softDailyMin) {
      if (oneDayFirst) {
        leftovers = round(b, leftovers.map((l) => l.gap), maxDailyMin, oneDay);
      }
      leftovers = round(b, leftovers.map((l) => l.gap), maxDailyMin);
    }
    const unfilled: AutofillUnfilled[] = leftovers.map(({ gap, counts }) => ({
      ...gap,
      reason: unfilledReason(counts, input.volunteers.length),
      counts,
    }));
    for (let i = unfilled.length - 1; i >= 0; i -= 1) {
      const gap = unfilled[i]!;
      if (repair(gap, input, b.shifts, b.load, breadth, tiers, b.proposals, take(b))) {
        unfilled.splice(i, 1);
      }
    }
    return { b, unfilled };
  };

  const oneDay = input.volunteers.filter(isSingleDay);
  // Rounds 2a+2b are a preference, so they are allowed to shuffle who carries a
  // long day and not to cost a post its cover. That cannot be argued from the
  // structure — round 2a can spend the very slack the repair pass was going to
  // use — so it is settled per plan instead: build both from the shared round 1
  // and keep whichever covers more, the one-day version on a tie.
  const plain = finish(false);
  const mixedRoster = oneDay.length > 0 && oneDay.length < input.volunteers.length;
  const preferred = mixedRoster && maxDailyMin > softDailyMin ? finish(true) : null;
  // Minutes first, so the preference can never cost cover; then the urgent
  // posts, because two plans covering the same total are not equally good if
  // one of them leaves a `nujen` post empty; and only then the preference.
  const better = preferred
    ? coveredMin(preferred.b) - coveredMin(plain.b) ||
      urgentUnfilledMin(plain.unfilled) - urgentUnfilledMin(preferred.unfilled)
    : -1;
  const { b, unfilled } = preferred && better >= 0 ? preferred : plain;

  const ordered = [...b.proposals.values()].sort(
    (a, x) => a.gap.fromMin - x.gap.fromMin || a.gap.postCode.localeCompare(x.gap.postCode, 'sl'),
  );
  return {
    proposals: ordered,
    unfilled: unfilled.sort(
      (a, x) => a.fromMin - x.fromMin || a.postCode.localeCompare(x.postCode, 'sl'),
    ),
    utilisation: utilisation(input, b.load, ordered, softDailyMin),
  };
}

/**
 * One-level swap. A piece went unfilled because everyone who could take it was
 * already busy; if one of those people holds a *proposed* piece that somebody
 * else can take, hand that piece over and free them for this one.
 */
function repair(
  gap: AutofillGap,
  input: AutofillInput,
  shifts: AutofillShift[],
  load: Map<string, number>,
  breadth: Map<string, number>,
  tiers: Map<string, ExperienceTier>,
  proposals: Map<AutofillGap, AutofillProposal>,
  take: (gap: AutofillGap, pick: Candidate) => void,
): boolean {
  const cap = input.planning.maxDailyMin;
  const blocked = [...proposals.entries()].sort(
    (a, b) => a[0].fromMin - b[0].fromMin || a[0].postCode.localeCompare(b[0].postCode, 'sl'),
  );

  for (const [heldGap, held] of blocked) {
    // Only a piece that actually stands in the way is worth moving.
    if (!rangesOverlap(gap, heldGap) && gapBetween(gap, heldGap) >= MIN_GAP_MINUTES) continue;

    const without = shifts.filter((s) => s.id !== proposedShift(heldGap, held.volunteerId).id);
    const relieved = new Map(load);
    relieved.set(held.volunteerId, (relieved.get(held.volunteerId) ?? 0) - (heldGap.toMin - heldGap.fromMin));

    // Can the person we would free actually take the unfilled piece now?
    const forGap = assess(gap, input, without, relieved, tiers, cap);
    const taker = [...forGap.clean, ...forGap.warned].find((c) => c.volunteer.id === held.volunteerId);
    if (!taker) continue;

    // …and can anyone else pick up the piece they are letting go of?
    const forHeld = assess(heldGap, input, without, relieved, tiers, cap, held.volunteerId);
    const replacement = poolOf(forHeld).sort(rank(relieved, breadth, heldGap, cap)).at(0);
    if (!replacement) continue;

    // Commit both moves.
    shifts.splice(0, shifts.length, ...without);
    load.set(held.volunteerId, relieved.get(held.volunteerId)!);
    proposals.delete(heldGap);
    take(heldGap, replacement);
    take(gap, taker);
    return true;
  }
  return false;
}

function utilisation(
  input: AutofillInput,
  load: Map<string, number>,
  proposals: AutofillProposal[],
  softDailyMin: number,
): AutofillUtilisation[] {
  return input.volunteers
    .map((v) => {
      const plannedMin = load.get(v.id) ?? v.dayMinutes;
      return {
        volunteerId: v.id,
        volunteerName: v.name,
        plannedMin,
        availableMin: availableMinutes(v.availability, input.dayKey),
        proposedCount: proposals.filter((p) => p.volunteerId === v.id).length,
        needsReconfirm: v.briefed,
        overSoft: plannedMin > softDailyMin,
      };
    })
    .filter((u) => u.proposedCount > 0)
    .sort((a, b) => b.plannedMin - a.plannedMin || a.volunteerName.localeCompare(b.volunteerName, 'sl'));
}

function unfilledReason(
  counts: AutofillUnfilled['counts'],
  poolSize: number,
): AutofillUnfilled['reason'] {
  if (poolSize === 0) return 'no-pool';
  if (counts.available > 0) return 'busy';
  if (counts.atCapacity > 0 && counts.atCapacity >= counts.notAvailable) return 'at-capacity';
  if (counts.notAvailable >= counts.declined) return 'not-available';
  return 'declined';
}
